// Agent-related TypeScript interfaces

export interface Agent {
  id: string;
  name: string;
  agent_type: AgentType;
  system_prompt: string;
  model_id: string;
  parameters?: AgentParameters;
  is_active: boolean;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

export interface AgentParameters {
  temperature?: number;
  max_tokens?: number;
  top_p?: number;
  top_k?: number;
  stop_sequences?: string[];
  seed?: number;
  response_format?: string;
}

export type AgentType =
  | "salary_writer"
  | "content_writer"
  | "technical_writer"
  | "general_assistant";

export interface AgentCreateRequest {
  name: string;
  agent_type: AgentType;
  system_prompt: string;
  model_id?: string;
  parameters?: AgentParameters;
  is_active?: boolean;
  created_by?: string;
}

export interface AgentUpdateRequest {
  name?: string;
  agent_type?: AgentType;
  system_prompt?: string;
  model_id?: string;
  parameters?: AgentParameters;
  is_active?: boolean;
}

export interface AgentStats {
  conversation_count: number;
  message_count: number;
  average_latency_ms?: number;
  success_rate: number;
  last_used_at?: string;
  popular_workflows: string[];
}

export interface AgentDetail extends Agent {
  stats: AgentStats;
  active_sessions: number;
}

export interface AgentListResponse {
  agents: Agent[];
  total: number;
  page: number;
  size: number;
  total_pages: number;
}

export interface AgentTestRequest {
  message: string;
  conversation_id?: string;
  validate_output?: boolean;
  use_pydantic_ai?: boolean;
}

export interface AgentTestResponse {
  response: string;
  conversation_id: string;
  latency_ms: number;
  validation_result?: {
    is_valid: boolean;
    validated_data?: any;
    errors?: string[];
  };
}

export interface WorkflowInfo {
  workflow_id: string;
  name: string;
  description: string;
  steps: number;
  initial_step: string;
}

export interface WorkflowListResponse {
  workflows: WorkflowInfo[];
}

// Agent type display information
export const AGENT_TYPE_INFO: Record<
  AgentType,
  {
    label: string;
    description: string;
    color: string;
  }
> = {
  salary_writer: {
    label: "Salary Writer",
    description:
      "Specialized in salary analysis and compensation recommendations",
    color: "bg-brand-teal/10 text-brand-teal",
  },
  content_writer: {
    label: "Content Writer",
    description:
      "Creates engaging content across various formats and audiences",
    color: "bg-[rgba(72,178,205,0.1)] text-brand-teal",
  },
  technical_writer: {
    label: "Technical Writer",
    description: "Produces technical documentation and API guides",
    color: "bg-purple-100 text-purple-800",
  },
  general_assistant: {
    label: "General Assistant",
    description: "Flexible assistant for various tasks and inquiries",
    color: "bg-brand-surface text-brand-text",
  },
};

// Available LLM models
export const AVAILABLE_MODELS = [
  {
    id: "openai.gpt-oss-20b-1:0",
    name: "GPT OSS 20B",
    description: "OpenAI GPT open source model",
  },
];

// Default parameters for different agent types
export const DEFAULT_AGENT_PARAMETERS: Record<AgentType, AgentParameters> = {
  salary_writer: {
    temperature: 0.3,
    max_tokens: 2000,
    top_p: 0.9,
  },
  content_writer: {
    temperature: 0.7,
    max_tokens: 3000,
    top_p: 0.9,
  },
  technical_writer: {
    temperature: 0.2,
    max_tokens: 4000,
    top_p: 0.8,
  },
  general_assistant: {
    temperature: 0.5,
    max_tokens: 2000,
    top_p: 0.9,
  },
};
